import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import useEditorLocationDescriptor from 'app/shared/components/forms/FormEditor/hooks/useEditorLocationDescriptor';
import PrePreviewFormBasedOnLoanModal from 'app/shared/components/forms/PrePreviewFormBasedOnLoanModal';
import PrePreviewFormModal from 'app/shared/components/forms/PrePreviewFormModal';
import _ from 'lodash';

import {
  FormOverrideTemplateType,
  FormTemplateTypeNewApi,
  FormViewerNavigationType,
  NavigationStateType,
} from 'constants/forms';
import FORM_PREVIEW_STYLES from 'styles/formPreviewStyles';
import { getConditionalObject } from 'utils/data';
import { jsonStringify } from 'utils/json';

import {
  createFormOverrideAction,
  deleteFormAction,
  openFormPreviewAction,
  setFormNavigationStateTypeAction,
  updateFormTemplateAction,
} from 'store/forms/actions';
import { selectFormNavigationType } from 'store/forms/selectors';
import { openDialogAction } from 'store/global/actions';

const useFormTemplateActions = (setIsLoading = _.noop) => {
  const dispatch = useDispatch();
  const { push, replace } = useHistory();

  const navigationType = useSelector(selectFormNavigationType);
  const isNavigationRequired = navigationType === FormViewerNavigationType.URL;
  const editorLocationDescriptor = useEditorLocationDescriptor();

  const onSaveContent = useCallback(
    async ({ content, formId, state, formType, selectedPage = 1, document = null }) => {
      setIsLoading(true);

      const inlineData = {
        type: content ? FormTemplateTypeNewApi.HTML_FILLED : FormTemplateTypeNewApi.HTML_EMPTY,
        formId,
        ...getConditionalObject(!_.isNil(document), () => ({
          templateJson: jsonStringify(document),
        })),
      };

      // A save that carries a Prism document sends Prism's HTML as it is: inlining CKEditor's
      // stylesheet would add its rules and drop Prism's own style attributes.
      if (content !== null) {
        inlineData.content = _.isNil(document)
          ? (await import('juice')).default.inlineContent(
              content,
              `<styles>${FORM_PREVIEW_STYLES.replaceAll('.ck-content', '')}</styles>`,
            )
          : content;
      }

      return dispatch(updateFormTemplateAction({ ...inlineData, document }))
        .then(() => {
          if (isNavigationRequired) {
            FormsPaths.viewForm.navigateWith(
              push,
              {
                [FORMS_ROUTING_PARAMS.formId]: formId,
                [FORMS_ROUTING_PARAMS.area]: state,
                [FORMS_ROUTING_PARAMS.formType]: formType,
                [FORMS_ROUTING_PARAMS.selectedPage]: selectedPage,
              },
              editorLocationDescriptor,
            );

            return;
          }

          dispatch(setFormNavigationStateTypeAction(NavigationStateType.VIEW));
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, isNavigationRequired, push, setIsLoading, editorLocationDescriptor],
  );

  const onOverrideForm = useCallback(
    ({ content, formId, state, formType, withPageNumbers, selectedPage = 1, document = null }) => {
      const formData = new FormData();
      formData.append(
        'body',
        jsonStringify({
          formId,
          formOverrideTemplate: { type: FormOverrideTemplateType.EXISTS_FORM_CONTENT },
          withPageNumbers,
        }),
      );

      setIsLoading(true);

      return dispatch(createFormOverrideAction({ formId, formData }))
        .then(() => onSaveContent({ content, formId, state, formType, selectedPage, document }))
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, onSaveContent, setIsLoading],
  );

  const onDeleteForm = useCallback(
    ({ formId, state, formType }) => {
      setIsLoading(true);

      return dispatch(deleteFormAction({ formId }))
        .then(() => {
          if (isNavigationRequired) {
            FormsPaths.viewForm.navigateWith(
              replace,
              {
                [FORMS_ROUTING_PARAMS.formId]: formId,
                [FORMS_ROUTING_PARAMS.area]: state,
                [FORMS_ROUTING_PARAMS.formType]: formType,
              },
              editorLocationDescriptor,
            );

            return;
          }

          dispatch(setFormNavigationStateTypeAction(NavigationStateType.VIEW));
        })
        .finally(() => setIsLoading(false));
    },
    [dispatch, isNavigationRequired, replace, setIsLoading, editorLocationDescriptor],
  );

  const onViewForm = useCallback(
    ({ formId, state, formType, selectedPage }) => {
      if (isNavigationRequired) {
        FormsPaths.viewForm.navigateWith(
          push,
          {
            [FORMS_ROUTING_PARAMS.formId]: formId,
            [FORMS_ROUTING_PARAMS.area]: state,
            [FORMS_ROUTING_PARAMS.formType]: formType,
            [FORMS_ROUTING_PARAMS.selectedPage]: selectedPage,
          },
          editorLocationDescriptor,
        );

        return;
      }

      dispatch(setFormNavigationStateTypeAction(NavigationStateType.VIEW));
    },
    [dispatch, isNavigationRequired, push, editorLocationDescriptor],
  );

  const onEditForm = useCallback(
    ({ formId, state, formType, selectedPage }) => {
      if (isNavigationRequired) {
        FormsPaths.editForm.navigateWith(
          push,
          {
            [FORMS_ROUTING_PARAMS.formId]: formId,
            [FORMS_ROUTING_PARAMS.area]: state,
            [FORMS_ROUTING_PARAMS.formType]: formType,
            [FORMS_ROUTING_PARAMS.selectedPage]: selectedPage,
          },
          editorLocationDescriptor,
        );

        return;
      }

      dispatch(setFormNavigationStateTypeAction(NavigationStateType.EDIT));
    },
    [dispatch, isNavigationRequired, push, editorLocationDescriptor],
  );

  const onPreviewForm = useCallback(
    ({ isSignersNumberSpecifyingNeeded, ...props }) => {
      if (isSignersNumberSpecifyingNeeded) {
        return dispatch(
          openDialogAction({
            header: 'Specify the number of signers',
            body: dialogProps => <PrePreviewFormModal {...props} {...dialogProps} />,
          }),
        );
      }

      dispatch(openFormPreviewAction(props));
    },
    [dispatch],
  );

  const onPreviewWithLoanForm = useCallback(
    props => {
      return dispatch(
        openDialogAction({
          header: 'Choose loan to preview form',
          body: dialogProps => <PrePreviewFormBasedOnLoanModal {...props} {...dialogProps} />,
        }),
      );
    },
    [dispatch],
  );

  return {
    onSaveContent,
    onOverrideForm,
    onDeleteForm,
    onViewForm,
    onEditForm,
    onPreviewForm,
    onPreviewWithLoanForm,
  };
};

export default useFormTemplateActions;
